/**
 * 🔔 Notification Bell — مع dropdown متقدّم
 *
 * Features:
 *   - Portal-based positioning (يخرج من الـ Sidebar)
 *   - Grouping (اليوم، الأمس، أقدم)
 *   - Empty state أنيق
 *   - Loading state
 *   - Real-time stream
 *   - زر "عرض الكل" لصفحة /notifications
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  CheckCheck,
  X,
  Trash2,
  Inbox,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  normalizeNotificationRoute,
  type AppNotification,
} from '@application/services/NotificationService';
import { useNotificationsStore } from '@application/stores/notifications.store';
import { cn } from '@shared/utils/cn';

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // مصدر موحّد (store) — يدمج firestore + الحجوزات الحيّة.
  // الاشتراك reference-counted، فلا تتضاعف الـ streams رغم تركيب الجرس
  // مرتين في MainLayout (سطح مكتب + جوال).
  const notifications = useNotificationsStore((s) => s.notifications);
  const loading = useNotificationsStore((s) => s.loading);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const initNotifications = useNotificationsStore((s) => s.init);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const removeNotification = useNotificationsStore((s) => s.remove);

  useEffect(() => initNotifications(), [initNotifications]);

  // حساب موضع الـ dropdown (أسفل الـ bell، يفتح في الاتجاه المتاح)
  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const dropdownWidth = 380;
    const viewportWidth = window.innerWidth;

    // في RTL، الـ bell في sidebar اليمين. نفتح الـ dropdown باتجاه يسار الشاشة
    // (نحو الـ main content)
    let right = viewportWidth - rect.right;

    // لو الـ dropdown ما يطلع للشاشة، نعدّل
    if (right + dropdownWidth > viewportWidth - 16) {
      right = viewportWidth - dropdownWidth - 16;
    }
    if (right < 8) right = 8;

    setPosition({
      top: rect.bottom + 8,
      right,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  // Close on click outside (للـ dropdown اللي في portal)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open]);

  // عند تمرير الصفحة أو تغيير الحجم: نتتبّع موضع الجرس (النافذة تبقى مفتوحة).
  // نتجاهل التمرير الذي يحدث داخل النافذة نفسها — لا يحرّك الجرس.
  useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      updatePosition();
    };
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  const handleClick = async (n: AppNotification) => {
    await markAsRead(n);
    if (n.route) navigate(normalizeNotificationRoute(n.route));
    setOpen(false);
  };

  const handleMarkAll = async () => {
    try {
      const count = await markAllAsRead();
      if (count > 0) toast.success(`تم تحديد ${count} كمقروء`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleDelete = async (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    try {
      await removeNotification(n);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const grouped = groupByDate(notifications);

  return (
    <>
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen((s) => !s)}
        className={cn(
          'relative p-2.5 rounded-xl transition-all',
          open
            ? 'bg-brand-purple-100 text-brand-purple-700'
            : 'hover:bg-brand-purple-50 text-brand-purple-600'
        )}
        aria-label="الإشعارات"
        aria-expanded={open}
      >
        <Bell className={cn('h-5 w-5 transition-transform', open && 'scale-110')} />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="absolute top-1 left-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md shadow-rose-300/40 ring-2 ring-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown via Portal — يخرج من الـ sidebar */}
      {typeof window !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && position && (
              <>
                {/* Backdrop خفيف للـ click outside */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40"
                  style={{ pointerEvents: 'none' }}
                />

                {/* Dropdown */}
                <motion.div
                  ref={dropdownRef}
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  style={{
                    position: 'fixed',
                    top: position.top,
                    right: position.right,
                    width: '380px',
                    maxWidth: 'calc(100vw - 32px)',
                    zIndex: 50,
                  }}
                  className="bg-white rounded-2xl shadow-2xl border border-brand-purple-100 overflow-hidden flex flex-col"
                  dir="rtl"
                >
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-brand-purple-100 flex items-center justify-between bg-gradient-to-l from-brand-purple-50 via-white to-brand-gold-50/40">
                    <div className="flex items-center gap-2">
                      <div className="grid place-items-center h-8 w-8 rounded-lg bg-gradient-to-br from-brand-purple-500 to-brand-gold-500 text-white shadow-sm">
                        <Bell className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-brand-purple-900 leading-tight">
                          الإشعارات
                        </h3>
                        {unreadCount > 0 && (
                          <p className="text-[10px] text-brand-purple-500 font-semibold">
                            {unreadCount} جديدة
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAll}
                          title="تحديد الكل كمقروء"
                          className="p-1.5 rounded-lg hover:bg-white text-brand-purple-600 transition-colors"
                        >
                          <CheckCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setOpen(false)}
                        className="p-1.5 rounded-lg hover:bg-white text-brand-purple-500 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* List */}
                  <div className="flex-1 overflow-y-auto max-h-[60vh] min-h-[120px]">
                    {loading ? (
                      <LoadingState />
                    ) : notifications.length === 0 ? (
                      <EmptyState />
                    ) : (
                      <div>
                        {grouped.map((group) => (
                          <div key={group.label}>
                            <div className="sticky top-0 bg-brand-purple-50/80 backdrop-blur-sm px-4 py-1.5 text-[10px] font-bold text-brand-purple-600 uppercase tracking-wider border-b border-brand-purple-100">
                              {group.label}
                            </div>
                            {group.items.map((n) => (
                              <NotificationItem
                                key={n.id}
                                notification={n}
                                onClick={() => handleClick(n)}
                                onDelete={(e) => handleDelete(e, n)}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <button
                      onClick={() => {
                        navigate('/notifications');
                        setOpen(false);
                      }}
                      className="px-4 py-3 text-xs font-bold text-brand-purple-700 hover:bg-brand-purple-50 border-t border-brand-purple-100 transition-colors flex items-center justify-center gap-1.5"
                    >
                      عرض كل الإشعارات
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </button>
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}

// ═══════════════════════════════════════════════════
// Notification Item
// ═══════════════════════════════════════════════════
function NotificationItem({
  notification,
  onClick,
  onDelete,
}: {
  notification: AppNotification;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const ic = getNotificationIcon(notification.type);

  return (
    <motion.div
      initial={{ opacity: 0, x: 5 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -5 }}
      className={cn(
        'group relative flex items-start gap-3 px-4 py-3 hover:bg-brand-purple-50/50 transition-colors cursor-pointer border-b border-brand-purple-50 last:border-b-0',
        !notification.isRead && 'bg-brand-purple-50/30'
      )}
      onClick={onClick}
    >
      {/* Unread accent bar */}
      {!notification.isRead && (
        <div className="absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-purple-500 to-brand-gold-500" />
      )}

      {/* Icon */}
      <div
        className={cn(
          'shrink-0 grid place-items-center h-9 w-9 rounded-xl text-base shadow-sm',
          ic.bg
        )}
      >
        {ic.emoji}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'text-xs leading-tight pr-1',
              !notification.isRead
                ? 'font-bold text-brand-purple-900'
                : 'font-semibold text-brand-purple-700'
            )}
          >
            {notification.title}
          </p>
          {!notification.isRead && (
            <span className="shrink-0 w-2 h-2 mt-1 rounded-full bg-brand-purple-500 shadow-sm shadow-brand-purple-300" />
          )}
        </div>
        <p className="text-[11px] text-brand-purple-600 mt-1 leading-relaxed line-clamp-2">
          {notification.body}
        </p>
        <p className="text-[10px] text-brand-purple-400 mt-1 flex items-center gap-1">
          {formatRelative(notification.createdAt)}
        </p>
      </div>

      {/* Delete button */}
      <button
        onClick={onDelete}
        title="حذف"
        className="shrink-0 self-start p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-rose-50 text-rose-500 transition-all"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════
// Empty State (محسّن)
// ═══════════════════════════════════════════════════
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200 }}
        className="relative mb-4"
      >
        {/* الخلفية المتدرّجة */}
        <div className="absolute inset-0 -m-3 bg-gradient-to-br from-brand-purple-100/40 to-brand-gold-100/40 rounded-full blur-xl" />
        <div className="relative grid place-items-center w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-purple-50 to-brand-gold-50 border border-brand-purple-100 shadow-sm">
          <Inbox className="h-8 w-8 text-brand-purple-400" />
        </div>
        {/* sparkles زخرفية */}
        <span className="absolute -top-1 -right-1 text-sm">✨</span>
        <span className="absolute -bottom-1 -left-1 text-xs">💫</span>
      </motion.div>

      <h4 className="text-sm font-bold text-brand-purple-800 mb-1">
        ما في إشعارات حالياً
      </h4>
      <p className="text-xs text-brand-purple-500 leading-relaxed max-w-[260px]">
        لما يجي حجز جديد أو تقييم، راح يظهر هنا في الحال 🎉
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Loading State
// ═══════════════════════════════════════════════════
function LoadingState() {
  return (
    <div className="py-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex items-start gap-3 px-4 py-3 animate-pulse"
        >
          <div className="shrink-0 w-9 h-9 rounded-xl bg-brand-purple-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/3 bg-brand-purple-100 rounded" />
            <div className="h-2.5 w-full bg-brand-purple-50 rounded" />
            <div className="h-2 w-12 bg-brand-purple-50 rounded" />
          </div>
        </div>
      ))}
      <div className="flex items-center justify-center py-2">
        <Loader2 className="h-4 w-4 text-brand-purple-400 animate-spin" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════
interface NotificationGroup {
  label: string;
  items: AppNotification[];
}

function groupByDate(notifications: AppNotification[]): NotificationGroup[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups: NotificationGroup[] = [
    { label: 'اليوم', items: [] },
    { label: 'أمس', items: [] },
    { label: 'هذا الأسبوع', items: [] },
    { label: 'أقدم', items: [] },
  ];

  for (const n of notifications) {
    const d = n.createdAt;
    if (d >= today) groups[0]!.items.push(n);
    else if (d >= yesterday) groups[1]!.items.push(n);
    else if (d >= weekAgo) groups[2]!.items.push(n);
    else groups[3]!.items.push(n);
  }

  return groups.filter((g) => g.items.length > 0);
}

function getNotificationIcon(type: string): { emoji: string; bg: string } {
  const map: Record<string, { emoji: string; bg: string }> = {
    booking_created: {
      emoji: '🎉',
      bg: 'bg-gradient-to-br from-brand-purple-100 to-brand-purple-200',
    },
    booking_confirmed: {
      emoji: '✅',
      bg: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
    },
    booking_rejected: {
      emoji: '❌',
      bg: 'bg-gradient-to-br from-rose-100 to-rose-200',
    },
    booking_cancelled: {
      emoji: '🚫',
      bg: 'bg-gradient-to-br from-gray-100 to-gray-200',
    },
    booking_completed: {
      emoji: '🎊',
      bg: 'bg-gradient-to-br from-brand-gold-100 to-brand-gold-200',
    },
    booking_refunded: {
      emoji: '💸',
      bg: 'bg-gradient-to-br from-blue-100 to-blue-200',
    },
    pending_reminder: {
      emoji: '⏰',
      bg: 'bg-gradient-to-br from-amber-100 to-amber-200',
    },
    review_received: {
      emoji: '⭐',
      bg: 'bg-gradient-to-br from-amber-100 to-orange-200',
    },
    account_created: {
      emoji: '🎉',
      bg: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
    },
  };

  return (
    map[type] ?? {
      emoji: '🔔',
      bg: 'bg-gradient-to-br from-brand-purple-100 to-brand-purple-200',
    }
  );
}

function formatRelative(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (sec < 60) return 'الآن';
  if (min < 60) return `قبل ${min} دقيقة`;
  if (hr < 24) return `قبل ${hr} ساعة`;
  if (day < 7) return `قبل ${day} يوم`;
  return date.toLocaleDateString('ar', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
